import React, { useEffect, useState } from 'react';
import { useWedding } from '../contexts/WeddingContext';
import { useSeating } from '../contexts/wedding/useSeating';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Edit, Trash, User, Drag, Printer, Copy } from 'iconoir-react';
import {
  DndContext,
  DragOverlay,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { useDraggable, useDroppable } from '@dnd-kit/core';
import { toast } from 'sonner';

const TABLE_SIZE_LABELS = {
  'round_60': 'Round 60"',
  'round_72': 'Round 72"',
  'rect_30x72': 'Rect 30"x72"',
  'rect_30x96': 'Rect 30"x96"',
  'rect_48x96': 'Rect 48"x96"',
  'round': 'Round',
  'rectangular': 'Rectangle',
  'long': 'Long',
  'custom_round': 'Custom round',
  'custom_rect': 'Custom rectangle',
};

const TABLE_DEFAULT_CAPACITY = {
  'round_60': 8,
  'round_72': 10,
  'rect_30x72': 6,
  'rect_30x96': 8,
  'rect_48x96': 10,
};

// Render whatever label fits — built-in size, or the user-typed `custom_size_label`
// when the table is a `custom_*` variant.
const getTableSizeLabel = (table) => {
  // Backwards-compat: callers used to pass a raw `table_type` string. Detect that.
  if (typeof table === 'string') return TABLE_SIZE_LABELS[table] || table;
  if (table?.table_type?.startsWith('custom_') && table?.custom_size_label) {
    return table.custom_size_label;
  }
  return TABLE_SIZE_LABELS[table?.table_type] || table?.table_type || 'Custom';
};
const isRoundTable = (type) => type?.startsWith('round') || type === 'custom_round';

// Table diagram component
function TableDiagram({ tableType, seats, filledSeats = 0, size = 'md' }) {
  const isRound = isRoundTable(tableType);
  const cap = seats || TABLE_DEFAULT_CAPACITY[tableType] || 8;
  // 'sm' was 100x80 → bumped 50% to 150x120 per request. 'md' kept the same.
  const dims = size === 'sm' ? { w: 150, h: 120 } : { w: 160, h: 120 };

  if (isRound) {
    const cx = dims.w / 2;
    const cy = dims.h / 2;
    // Inner shapes scaled to match the new 'sm' canvas.
    const tableR = size === 'sm' ? 30 : 30;
    const chairR = size === 'sm' ? 33 : 34;
    const dotR = size === 'sm' ? 6 : 6;

    return (
      <svg width={dims.w} height={dims.h} viewBox={`0 0 ${dims.w} ${dims.h}`} className="mx-auto">
        {/* Table */}
        <circle cx={cx} cy={cy} r={tableR} fill="#FFFFFF" stroke="#CCC2BC" strokeWidth="2" />
        <text x={cx} y={cy + 3} textAnchor="middle" className="text-[10px] fill-muted font-mono">{filledSeats}/{cap}</text>
        {/* Chairs */}
        {Array.from({ length: cap }).map((_, i) => {
          const angle = (2 * Math.PI * i) / cap - Math.PI / 2;
          const x = cx + chairR * Math.cos(angle);
          const y = cy + chairR * Math.sin(angle);
          const filled = i < filledSeats;
          return <circle key={i} cx={x} cy={y} r={dotR} fill={filled ? '#D1B0AB' : '#FFFFFF'} stroke={filled ? '#B89690' : '#CCC2BC'} strokeWidth="1" />;
        })}
      </svg>
    );
  }

  // Rectangle table — sm widths bumped 50% to match the larger canvas.
  const isWide = tableType === 'rect_48x96';
  const tw = size === 'sm' ? (isWide ? 90 : 75) : (isWide ? 90 : 70);
  const th = size === 'sm' ? (isWide ? 36 : 27) : (isWide ? 36 : 26);
  const cx = dims.w / 2;
  const cy = dims.h / 2;
  const dotR = size === 'sm' ? 6 : 5;
  const halfTop = Math.ceil(cap / 2);
  const halfBot = cap - halfTop;

  return (
    <svg width={dims.w} height={dims.h} viewBox={`0 0 ${dims.w} ${dims.h}`} className="mx-auto">
      {/* Table */}
      <rect x={cx - tw / 2} y={cy - th / 2} width={tw} height={th} rx="4" fill="#FFFFFF" stroke="#CCC2BC" strokeWidth="2" />
      <text x={cx} y={cy + 3} textAnchor="middle" className="text-[10px] fill-muted font-mono">{filledSeats}/{cap}</text>
      {/* Top row chairs */}
      {Array.from({ length: halfTop }).map((_, i) => {
        const spacing = tw / (halfTop + 1);
        const x = cx - tw / 2 + spacing * (i + 1);
        const y = cy - th / 2 - dotR - 4;
        const filled = i < filledSeats;
        return <circle key={`t${i}`} cx={x} cy={y} r={dotR} fill={filled ? '#D1B0AB' : '#FFFFFF'} stroke={filled ? '#B89690' : '#CCC2BC'} strokeWidth="1" />;
      })}
      {/* Bottom row chairs */}
      {Array.from({ length: halfBot }).map((_, i) => {
        const spacing = tw / (halfBot + 1);
        const x = cx - tw / 2 + spacing * (i + 1);
        const y = cy + th / 2 + dotR + 4;
        const idx = halfTop + i;
        const filled = idx < filledSeats;
        return <circle key={`b${i}`} cx={x} cy={y} r={dotR} fill={filled ? '#D1B0AB' : '#FFFFFF'} stroke={filled ? '#B89690' : '#CCC2BC'} strokeWidth="1" />;
      })}
    </svg>
  );
}

// Draggable guest chip
function DraggableGuest({ guest, onUnassign, showUnassign = false }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `guest-${guest.id}`,
    data: { guest },
  });

  const style = transform ? {
    transform: `translate(${transform.x}px, ${transform.y}px)`,
    zIndex: 50,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-1.5 px-3 py-1.5 bg-moss/10 text-moss border border-moss/20 rounded-full text-sm cursor-grab active:cursor-grabbing select-none ${isDragging ? 'shadow-lg ring-2 ring-terracotta/30' : ''}`}
      data-testid={`guest-chip-${guest.id}`}
    >
      <span {...attributes} {...listeners} className="cursor-grab">
        <Drag className="w-3 h-3 text-moss/40" />
      </span>
      <User className="w-3 h-3" />
      <span>{guest.name}</span>
      {guest.plus_ones > 0 && <span className="text-xs opacity-70">(+{guest.plus_ones})</span>}
      {showUnassign && (
        <button onClick={(e) => { e.stopPropagation(); onUnassign(guest.id); }} className="ml-1 hover:text-terracotta text-moss/60">
          <Trash className="w-3 h-3" />
        </button>
      )}
    </div>
  );
}

// Unassigned guest (draggable)
function UnassignedGuest({ guest }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: `unassigned-${guest.id}`,
    data: { guest },
  });

  const style = transform ? {
    transform: `translate(${transform.x}px, ${transform.y}px)`,
    zIndex: 50,
    opacity: isDragging ? 0.5 : 1,
  } : undefined;

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`flex items-center gap-2 p-2.5 bg-page rounded-sm cursor-grab active:cursor-grabbing select-none ${isDragging ? 'shadow-lg ring-2 ring-terracotta/30' : ''}`}
      data-testid={`unassigned-guest-${guest.id}`}
    >
      <Drag className="w-3 h-3 text-muted" />
      <span className="text-sm text-charcoal">{guest.name}</span>
      {guest.plus_ones > 0 && <span className="text-xs text-muted">(+{guest.plus_ones})</span>}
    </div>
  );
}

// Droppable table
function DroppableTable({ table, onUnassign, onEdit, onDelete, isOver }) {
  const { setNodeRef } = useDroppable({
    id: `table-${table.id}`,
    data: { table },
  });

  return (
    <Card
      ref={setNodeRef}
      className={`card-surface p-5 transition-all ${isOver ? 'ring-2 ring-terracotta/40 bg-terracotta/5' : ''}`}
      data-testid={`table-card-${table.id}`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <h3 className="font-serif text-lg text-charcoal">{table.table_name}</h3>
          <div className="flex items-center gap-3 text-xs text-muted mt-1">
            <Badge variant="outline" className="capitalize">{getTableSizeLabel(table)}</Badge>
            <span className="font-mono">{table.seats_filled}/{table.capacity} seats</span>
          </div>
        </div>
        <div className="flex gap-1">
          <button onClick={() => onEdit(table)} className="p-2 text-muted hover:text-charcoal"><Edit className="w-4 h-4" strokeWidth={1.5} /></button>
          <button onClick={() => onDelete(table.id)} className="p-2 text-muted hover:text-terracotta"><Trash className="w-4 h-4" strokeWidth={1.5} /></button>
        </div>
      </div>
      {table.notes && <p className="text-xs text-muted mb-3">{table.notes}</p>}

      {/* Table Diagram */}
      <div className="mb-3">
        <TableDiagram tableType={table.table_type} seats={table.capacity} filledSeats={table.seats_filled} size="sm" />
      </div>
      
      <div className="flex flex-wrap gap-2 min-h-[40px]">
        {table.guests.map((guest) => (
          <DraggableGuest key={guest.id} guest={guest} onUnassign={onUnassign} showUnassign />
        ))}
        {table.seats_filled < table.capacity && (
          <div className="flex items-center gap-1 px-3 py-1.5 border-2 border-dashed border-border rounded-full text-xs text-muted">
            {isOver ? 'Drop here' : `${table.capacity - table.seats_filled} seats open`}
          </div>
        )}
      </div>
    </Card>
  );
}

export default function SeatingPage() {
  const { currentWedding, events, guests, fetchEvents, fetchGuests } = useWedding();
  const [selectedEventId, setSelectedEventId] = useState('');
  const {
    tables,
    unassignedGuests: unassigned,
    createTable,
    updateTable,
    deleteTable,
    assignGuest,
    unassignGuest,
    copyFromEvent,
  } = useSeating(currentWedding?.id, selectedEventId);
  const [showTableModal, setShowTableModal] = useState(false);
  const [editingTable, setEditingTable] = useState(null);
  const [tableForm, setTableForm] = useState({ table_name: '', capacity: '10', table_type: 'round_60', custom_size_label: '', notes: '' });
  const [activeId, setActiveId] = useState(null);
  const [overTableId, setOverTableId] = useState(null);
  const [showPrintView, setShowPrintView] = useState(false);
  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copySourceEventId, setCopySourceEventId] = useState('');
  const [copyAssignments, setCopyAssignments] = useState(true);
  const [copyLoading, setCopyLoading] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  useEffect(() => {
    if (currentWedding) {
      fetchEvents(currentWedding.id);
      fetchGuests(currentWedding.id);
    }
  }, [currentWedding, fetchEvents, fetchGuests]);

  useEffect(() => {
    if (events.length > 0 && !selectedEventId) {
      setSelectedEventId(events[0].id);
    }
  }, [events, selectedEventId]);

  // useSeating auto-fetches on weddingId/eventId change; no manual effect needed.

  const handleOpenTableModal = (table = null) => {
    if (table) {
      setEditingTable(table);
      setTableForm({
        table_name: table.table_name,
        capacity: table.capacity?.toString() || '10',
        table_type: table.table_type || 'round_60',
        custom_size_label: table.custom_size_label || '',
        notes: table.notes || '',
      });
    } else {
      setEditingTable(null);
      setTableForm({ table_name: '', capacity: '10', table_type: 'round_60', custom_size_label: '', notes: '' });
    }
    setShowTableModal(true);
  };

  const handleSaveTable = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        table_name: tableForm.table_name,
        capacity: parseInt(tableForm.capacity, 10) || 10,
        table_type: tableForm.table_type,
        custom_size_label: tableForm.table_type?.startsWith('custom_')
          ? (tableForm.custom_size_label || '').trim() || null
          : null,
        notes: tableForm.notes,
      };
      if (editingTable) {
        await updateTable(editingTable.id, payload);
      } else {
        await createTable({
          wedding_id: currentWedding.id,
          event_id: selectedEventId,
          ...payload,
        });
      }
      setShowTableModal(false);
    } catch (err) { toast.error('Failed to save table'); }
  };

  const handleDeleteTable = async (tableId) => {
    if (window.confirm('Delete this table and unassign all guests?')) {
      await deleteTable(tableId);
    }
  };

  const handleAssignGuest = async (tableId, guestId) => {
    try {
      await assignGuest(tableId, guestId);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to assign guest');
    }
  };

  const handleUnassignGuest = async (guestId) => {
    await unassignGuest(guestId);
  };

  const handleOpenCopyDialog = () => {
    // Default-select the first event that *isn't* the active one.
    const firstOther = events.find((e) => e.id !== selectedEventId);
    setCopySourceEventId(firstOther ? firstOther.id : '');
    setCopyAssignments(true);
    setShowCopyDialog(true);
  };

  const handleCopySeating = async () => {
    if (!copySourceEventId) {
      toast.error('Pick an event to copy from.');
      return;
    }
    setCopyLoading(true);
    try {
      const data = await copyFromEvent({
        sourceEventId: copySourceEventId,
        copyAssignments,
      });
      const tablesCount = data?.tables_created || 0;
      const assignmentsCount = data?.assignments_created || 0;
      const parts = [`${tablesCount} table${tablesCount === 1 ? '' : 's'} copied`];
      if (copyAssignments) {
        parts.push(`${assignmentsCount} guest${assignmentsCount === 1 ? '' : 's'} reseated`);
      }
      toast.success(parts.join(' · '));
      setShowCopyDialog(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not copy seating chart.');
    } finally {
      setCopyLoading(false);
    }
  };

  // DnD handlers
  const handleDragStart = (event) => {
    setActiveId(event.active.id);
  };

  const handleDragOver = (event) => {
    const { over } = event;
    if (over && over.id.toString().startsWith('table-')) {
      setOverTableId(over.id.toString().replace('table-', ''));
    } else {
      setOverTableId(null);
    }
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;
    setActiveId(null);
    setOverTableId(null);

    if (!over) return;

    const guestId = active.data.current?.guest?.id;
    if (!guestId) return;

    const overStr = over.id.toString();
    if (overStr.startsWith('table-')) {
      const tableId = overStr.replace('table-', '');
      handleAssignGuest(tableId, guestId);
    }
  };

  const totalSeats = tables.reduce((s, t) => s + (t.capacity || 0), 0);
  const filledSeats = tables.reduce((s, t) => s + (t.seats_filled || 0), 0);

  // Find active guest for drag overlay
  const activeGuest = activeId ? (() => {
    const gid = activeId.replace('guest-', '').replace('unassigned-', '');
    for (const t of tables) {
      const g = t.guests.find(g => g.id === gid);
      if (g) return g;
    }
    return unassigned.find(g => g.id === gid);
  })() : null;

  if (!currentWedding) {
    return <Layout><div className="text-center py-12 text-muted">Please select or create a wedding first.</div></Layout>;
  }

  return (
    <Layout>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragOver={handleDragOver} onDragEnd={handleDragEnd}>
        <div className="space-y-8" data-testid="seating-page">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="font-serif font-light text-4xl sm:text-5xl text-charcoal tracking-tight">Seating Chart</h1>
              <p className="text-muted font-light mt-2">
                Each event has its own chart — switch between events to build separate seating layouts for Mehndi, Reception, and beyond.
              </p>
            </div>
            <div className="flex items-center gap-3">
              <Select value={selectedEventId} onValueChange={setSelectedEventId}>
                <SelectTrigger className="w-48 h-10" data-testid="seating-event-select">
                  <SelectValue placeholder="Select event" />
                </SelectTrigger>
                <SelectContent>
                  {events.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={() => handleOpenTableModal()} className="btn-primary flex items-center gap-2" data-testid="add-table-btn">
                <Plus className="w-4 h-4" /> Add Table
              </Button>
              {events.length > 1 && (
                <Button
                  onClick={handleOpenCopyDialog}
                  className="btn-secondary flex items-center gap-2"
                  data-testid="copy-seating-btn"
                >
                  <Copy className="w-4 h-4" /> Copy from event
                </Button>
              )}
              {tables.length > 0 && (
                <Button onClick={() => setShowPrintView(true)} className="btn-secondary flex items-center gap-2" data-testid="print-seating-btn">
                  <Printer className="w-4 h-4" /> Print
                </Button>
              )}
            </div>
          </div>

          {/* Summary */}
          <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
            <Card className="card-surface p-6">
              <p className="text-sm text-muted uppercase tracking-wider mb-1">Tables</p>
              <p className="font-mono text-2xl text-charcoal">{tables.length}</p>
            </Card>
            <Card className="card-surface p-6">
              <p className="text-sm text-muted uppercase tracking-wider mb-1">Total Seats</p>
              <p className="font-mono text-2xl text-charcoal">{totalSeats}</p>
            </Card>
            <Card className="card-surface p-6">
              <p className="text-sm text-muted uppercase tracking-wider mb-1">Seated</p>
              <p className="font-mono text-2xl text-moss">{filledSeats}</p>
            </Card>
            <Card className="card-surface p-6">
              <p className="text-sm text-muted uppercase tracking-wider mb-1">Unassigned</p>
              <p className="font-mono text-2xl text-terracotta">{unassigned.length}</p>
            </Card>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Tables */}
            <div className="lg:col-span-2 space-y-4">
              {tables.length === 0 ? (
                <Card className="card-surface p-12 text-center">
                  <p className="text-muted mb-4">No tables created for this event yet</p>
                  <Button onClick={() => handleOpenTableModal()} className="btn-primary">Create First Table</Button>
                </Card>
              ) : (
                tables.map((table) => (
                  <DroppableTable
                    key={table.id}
                    table={table}
                    onUnassign={handleUnassignGuest}
                    onEdit={handleOpenTableModal}
                    onDelete={handleDeleteTable}
                    isOver={overTableId === table.id}
                  />
                ))
              )}
            </div>

            {/* Unassigned Panel */}
            <div>
              <Card className="card-surface p-5 sticky top-20">
                <h3 className="font-serif text-lg text-charcoal mb-1">Unassigned Guests</h3>
                <p className="text-xs text-muted mb-4">Drag guests to a table or use the dropdown</p>
                {unassigned.length === 0 ? (
                  <p className="text-sm text-muted text-center py-4">All confirmed guests are seated</p>
                ) : (
                  <div className="space-y-2 max-h-96 overflow-y-auto">
                    {unassigned.map((guest) => (
                      <UnassignedGuest key={guest.id} guest={guest} />
                    ))}
                  </div>
                )}
              </Card>
            </div>
          </div>
        </div>

        {/* Drag Overlay */}
        <DragOverlay>
          {activeGuest && (
            <div className="flex items-center gap-1.5 px-3 py-1.5 bg-terracotta/10 text-terracotta border border-terracotta/30 rounded-full text-sm shadow-lg">
              <User className="w-3 h-3" />
              <span>{activeGuest.name}</span>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      {/* Table Modal */}
      <Dialog open={showTableModal} onOpenChange={setShowTableModal}>
        <DialogContent className="bg-white max-w-md">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">{editingTable ? 'Edit Table' : 'Add Table'}</DialogTitle>
            <DialogDescription className="text-muted">Configure your table for seating arrangements.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSaveTable} className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Table Name</Label>
              <Input value={tableForm.table_name} onChange={(e) => setTableForm({ ...tableForm, table_name: e.target.value })}
                className="input-field h-12" placeholder="e.g., Table 1, Head Table" required data-testid="table-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Capacity</Label>
                <Input type="number" value={tableForm.capacity} onChange={(e) => setTableForm({ ...tableForm, capacity: e.target.value })}
                  className="input-field h-12 font-mono" min="1" max="50" data-testid="table-capacity-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Table Size</Label>
                <Select value={tableForm.table_type} onValueChange={(v) => {
                  const cap = TABLE_DEFAULT_CAPACITY[v];
                  setTableForm({ ...tableForm, table_type: v, ...(cap ? { capacity: cap.toString() } : {}) });
                }}>
                  <SelectTrigger className="input-field h-12" data-testid="table-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="round_60">Round 60" (seats 8)</SelectItem>
                    <SelectItem value="round_72">Round 72" (seats 10)</SelectItem>
                    <SelectItem value="rect_30x72">Rectangle 30"x72" (seats 6)</SelectItem>
                    <SelectItem value="rect_30x96">Rectangle 30"x96" (seats 8)</SelectItem>
                    <SelectItem value="rect_48x96">Rectangle 48"x96" (seats 10)</SelectItem>
                    <SelectItem value="custom_round">Custom — round</SelectItem>
                    <SelectItem value="custom_rect">Custom — rectangle</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            {tableForm.table_type?.startsWith('custom_') && (
              <div className="space-y-2" data-testid="custom-table-size-section">
                <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Custom size label</Label>
                <Input
                  value={tableForm.custom_size_label}
                  onChange={(e) => setTableForm({ ...tableForm, custom_size_label: e.target.value })}
                  className="input-field h-12"
                  placeholder='e.g., Round 84" or 6ft long'
                  data-testid="custom-size-label-input"
                />
                <p className="text-[11px] text-muted leading-relaxed">
                  Set the capacity above to whatever this table seats. The label shows on the card and the printed chart.
                </p>
              </div>
            )}
            {/* Table Diagram Preview */}
            <div className="py-2 flex justify-center border border-border rounded-sm bg-page">
              <TableDiagram tableType={tableForm.table_type} seats={parseInt(tableForm.capacity) || 8} size="md" />
            </div>
            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Notes</Label>
              <Input value={tableForm.notes} onChange={(e) => setTableForm({ ...tableForm, notes: e.target.value })}
                className="input-field h-12" placeholder="e.g., Near the stage, VIP area" data-testid="table-notes-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" onClick={() => setShowTableModal(false)} className="btn-secondary flex-1">Cancel</Button>
              <Button type="submit" className="btn-primary flex-1" data-testid="save-table-btn">{editingTable ? 'Update' : 'Create'}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Copy seating chart from another event */}
      <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
        <DialogContent className="bg-white max-w-md" data-testid="copy-seating-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Copy seating chart</DialogTitle>
            <DialogDescription className="text-muted">
              Bring every table from another event into{' '}
              <span className="text-charcoal font-medium">
                {events.find((e) => e.id === selectedEventId)?.name || 'this event'}
              </span>
              . Existing tables on this event are kept — copied tables are added alongside them.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5 mt-4">
            <div className="space-y-2">
              <Label className="text-charcoal font-sans text-sm uppercase tracking-wider">Copy from</Label>
              <Select value={copySourceEventId} onValueChange={setCopySourceEventId}>
                <SelectTrigger className="input-field h-12" data-testid="copy-source-event-select">
                  <SelectValue placeholder="Select an event" />
                </SelectTrigger>
                <SelectContent>
                  {events
                    .filter((e) => e.id !== selectedEventId)
                    .map((e) => (
                      <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            <label className="flex items-start gap-3 cursor-pointer select-none p-3 rounded-sm bg-page border border-border">
              <input
                type="checkbox"
                checked={copyAssignments}
                onChange={(e) => setCopyAssignments(e.target.checked)}
                className="mt-1 accent-terracotta"
                data-testid="copy-assignments-checkbox"
              />
              <span className="text-sm text-charcoal leading-snug">
                Also copy guest assignments
                <span className="block text-xs text-muted mt-0.5">
                  Same guests will be auto-seated at the matching table. Uncheck to copy empty tables only.
                </span>
              </span>
            </label>
            <div className="flex gap-3 pt-2">
              <Button
                type="button"
                onClick={() => setShowCopyDialog(false)}
                className="btn-secondary flex-1"
                data-testid="copy-seating-cancel-btn"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={handleCopySeating}
                disabled={copyLoading || !copySourceEventId}
                className="btn-primary flex-1 flex items-center justify-center gap-2"
                data-testid="copy-seating-confirm-btn"
              >
                <Copy className="w-4 h-4" />
                {copyLoading ? 'Copying...' : 'Copy chart'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Printable Seating Chart */}
      <Dialog open={showPrintView} onOpenChange={setShowPrintView}>
        <DialogContent className="bg-white max-w-4xl max-h-[95vh] overflow-y-auto" data-testid="print-seating-dialog">
          <DialogHeader className="print:hidden">
            <DialogTitle className="font-serif text-2xl">Seating Chart</DialogTitle>
            <DialogDescription className="text-muted">
              {currentWedding?.name} — {events.find(e => e.id === selectedEventId)?.name}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-4" id="printable-seating">
            {/* Print Header */}
            <div className="text-center mb-8">
              <h1 className="font-serif text-3xl text-charcoal">{currentWedding?.name}</h1>
              <p className="text-lg text-muted font-light mt-1">{events.find(e => e.id === selectedEventId)?.name} — Seating Arrangement</p>
              {events.find(e => e.id === selectedEventId)?.event_date && (
                <p className="text-sm text-muted mt-1">
                  {new Date(events.find(e => e.id === selectedEventId).event_date).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
                </p>
              )}
              <div className="flex justify-center gap-6 mt-4 text-sm text-muted">
                <span>{tables.length} Tables</span>
                <span>{filledSeats} Guests Seated</span>
                <span>{totalSeats} Total Capacity</span>
              </div>
            </div>

            {/* Tables Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-6">
              {tables.map((table) => (
                <div key={table.id} className="border border-border rounded-lg p-4 text-center">
                  <div className={`w-20 h-20 mx-auto mb-3 ${isRoundTable(table.table_type) ? 'rounded-full' : 'rounded-lg'} border-2 border-charcoal/20 flex items-center justify-center bg-page`}>
                    <span className="font-mono text-sm text-muted">{table.seats_filled}/{table.capacity}</span>
                  </div>
                  <h3 className="font-serif text-base text-charcoal mb-1">{table.table_name}</h3>
                  <p className="text-xs text-muted mb-2">{getTableSizeLabel(table.table_type)}</p>
                  {table.notes && <p className="text-xs text-muted mb-2">{table.notes}</p>}
                  <div className="space-y-0.5">
                    {table.guests.map((guest) => (
                      <p key={guest.id} className="text-sm text-charcoal">{guest.name}</p>
                    ))}
                    {table.seats_filled < table.capacity && (
                      <p className="text-xs text-muted italic">{table.capacity - table.seats_filled} open seats</p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Unassigned List */}
            {unassigned.length > 0 && (
              <div className="mt-8 pt-6 border-t border-border">
                <h3 className="font-serif text-lg text-charcoal mb-3">Unassigned Guests ({unassigned.length})</h3>
                <div className="grid grid-cols-3 gap-1">
                  {unassigned.map((guest) => (
                    <p key={guest.id} className="text-sm text-muted">{guest.name}{guest.plus_ones > 0 && ` (+${guest.plus_ones})`}</p>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 mt-6 print:hidden">
            <Button onClick={() => setShowPrintView(false)} className="btn-secondary">Close</Button>
            <Button
              onClick={() => {
                const esc = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[c]));
                const eventName = events.find(e => e.id === selectedEventId)?.name || '';
                const html = `<!DOCTYPE html>
                  <html><head><title>Seating Chart - ${esc(currentWedding?.name)}</title>
                  <style>
                    body { font-family: 'Georgia', serif; color: #4A3E40; padding: 40px; }
                    h1 { font-size: 28px; font-weight: 300; margin: 0; }
                    h3 { font-size: 16px; font-weight: 400; }
                    p { margin: 2px 0; }
                    .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 24px; }
                    .table-card { border: 1px solid #CCC2BC; border-radius: 8px; padding: 16px; text-align: center; page-break-inside: avoid; }
                    .table-icon { width: 60px; height: 60px; margin: 0 auto 12px; border: 2px solid #CCC2BC; display: flex; align-items: center; justify-content: center; background: #FFFFFF; }
                    .round { border-radius: 50%; }
                    .rect { border-radius: 8px; }
                    .header { text-align: center; margin-bottom: 32px; }
                    .stats { display: flex; justify-content: center; gap: 24px; margin-top: 16px; font-size: 13px; color: #8B7B7D; }
                    .guest-name { font-size: 13px; }
                    .muted { color: #8B7B7D; font-size: 12px; }
                    .separator { border-top: 1px solid #CCC2BC; margin-top: 32px; padding-top: 24px; }
                    .unassigned-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 4px; }
                  </style></head><body>
                  <div class="header">
                    <h1>${esc(currentWedding?.name)}</h1>
                    <p style="font-size:16px;color:#8B7B7D;margin-top:4px;">${esc(eventName)} — Seating Arrangement</p>
                    <div class="stats"><span>${tables.length} Tables</span><span>${filledSeats} Guests Seated</span><span>${totalSeats} Total Capacity</span></div>
                  </div>
                  <div class="grid">
                    ${tables.map(t => `
                      <div class="table-card">
                        <div class="table-icon ${t.table_type?.startsWith('round') ? 'round' : 'rect'}">
                          <span class="muted">${t.seats_filled}/${t.capacity}</span>
                        </div>
                        <h3>${esc(t.table_name)}</h3>
                        <p class="muted" style="font-size:11px;margin-bottom:4px;">${esc(TABLE_SIZE_LABELS[t.table_type] || t.table_type)}</p>
                        ${t.notes ? `<p class="muted">${esc(t.notes)}</p>` : ''}
                        ${t.guests.map(g => `<p class="guest-name">${esc(g.name)}</p>`).join('')}
                        ${t.seats_filled < t.capacity ? `<p class="muted" style="font-style:italic;">${t.capacity - t.seats_filled} open seats</p>` : ''}
                      </div>
                    `).join('')}
                  </div>
                  ${unassigned.length > 0 ? `
                    <div class="separator">
                      <h3>Unassigned Guests (${unassigned.length})</h3>
                      <div class="unassigned-grid">
                        ${unassigned.map(g => `<p class="muted">${esc(g.name)}${g.plus_ones > 0 ? ` (+${g.plus_ones})` : ''}</p>`).join('')}
                      </div>
                    </div>
                  ` : ''}
                  </body></html>`;
                // Use a Blob URL so we never touch document.write (which is an XSS sink).
                // All dynamic text above is HTML-escaped via esc().
                const blob = new Blob([html], { type: 'text/html' });
                const url = URL.createObjectURL(blob);
                const printWin = window.open(url, '_blank');
                if (printWin) {
                  printWin.addEventListener('load', () => {
                    printWin.print();
                    setTimeout(() => URL.revokeObjectURL(url), 30000);
                  });
                }
              }}
              className="btn-primary flex items-center gap-2"
              data-testid="print-chart-btn"
            >
              <Printer className="w-4 h-4" /> Print Chart
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
